// ── session-reschedule-modal: the one-off reschedule confirm (Wave 6) ───────
// POST /sessions/:id/reschedule {new_date, new_time, new_timezone, notes} —
// canonical validator keys (2026-07-05 walk finding: the old scheduled_date/
// timezone/reason keys were never read by the API and 400'd).
// A one-off reschedule preserves the SAME session id (the backend continuity
// label). The page owns the actual POST + the toast (only after the backend
// confirms). A refused / closed write reads as its honest message inline and the
// dialog stays open — a closed write NEVER implies the session moved.

import { Alert, Button, Input, Modal } from "@vjal/ui";
import {  useState } from "react";

import type { CoachingSessionDto, SessionRescheduleRequest } from "../../services/coaching-dtos";
import { describeSessionWriteError } from "./sessions-helpers";
import { sessionStartInstantFromWallTime } from "./session-start";

export interface SessionRescheduleModalProps {
  open: boolean;
  session: CoachingSessionDto | null;
  onClose: () => void;
  onConfirm: (body: SessionRescheduleRequest) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function SessionRescheduleModal({
  open,
  session,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: SessionRescheduleModalProps) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [timezone, setTimezone] = useState("");
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState("");

  // Seed the timezone from the session on open; never fabricate a date/time.
  // Render adjustment on the open/timezone transition (bug-pack class, 2026-07-06).
  const seededTimezone = session?.timezone ?? "";
  const [prevSeed, setPrevSeed] = useState({ open, seededTimezone });
  if (open !== prevSeed.open || seededTimezone !== prevSeed.seededTimezone) {
    setPrevSeed({ open, seededTimezone });
    if (open) {
      if (date !== "") setDate("");
      if (time !== "") setTime("");
      if (timezone !== seededTimezone) setTimezone(seededTimezone);
      if (reason !== "") setReason("");
      if (fieldError !== "") setFieldError("");
    }
  }

  const topError = describeSessionWriteError(submitError);
  const canSubmit = date.trim().length > 0 && time.trim().length > 0 && timezone.trim().length > 0;

  function submit() {
    const newStart = sessionStartInstantFromWallTime(date.trim(), time.trim(), timezone.trim());
    // An unparseable timezone is the server's call; only a computed past start is blocked here.
    if (newStart && newStart.getTime() <= Date.now()) {
      setFieldError("Choose a new start in the future.");
      return;
    }
    setFieldError("");
    void onConfirm({
      new_date: date.trim(),
      new_time: time.trim(),
      new_timezone: timezone.trim(),
      ...(reason.trim() ? { notes: reason.trim() } : {})
    }).catch(() => {
      // The owning mutation surfaces the error via submitError.
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Reschedule session">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        <p className="m-0 text-sm text-ink-muted">
          The session keeps the same identity and any meeting continuity; past sessions are unaffected.
        </p>
        <Input
          label="New date"
          type="date"
          aria-label="New date"
          value={date}
          error={fieldError || undefined}
          onChange={(event) => {
            setDate(event.target.value);
            setFieldError("");
          }}
        />
        <Input
          label="Start time"
          type="time"
          aria-label="Start time"
          value={time}
          onChange={(event) => {
            setTime(event.target.value);
            setFieldError("");
          }}
        />
        <Input
          label="Timezone"
          aria-label="Timezone"
          placeholder="America/New_York"
          value={timezone}
          onChange={(event) => {
            setTimezone(event.target.value);
            setFieldError("");
          }}
        />
        <Input
          label="Reason (optional)"
          aria-label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            disabled={!canSubmit}
            onClick={submit}
          >
            Reschedule
          </Button>
        </div>
      </div>
    </Modal>
  );
}
